import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useEffect } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { DateTime } from 'luxon';
import { useCreateContentEntry, useUpdateContentEntry } from '@src/modules/settings/context/settings-api-hooks';

const schema = yup.object().shape({
  message: yup.string().required('Content is required'),
});

function BasicEntryEdit({
  isOpen, onClose, selectedEntry, typeKey, typeLabel, typeId,
}) {
  const { mutateAsync: doCreateContentEntry, isLoading: isCreating } = useCreateContentEntry(typeKey);
  const { mutateAsync: doUpdateContentEntry, isLoading: isUpdating } = useUpdateContentEntry(typeKey);

  const modalTitle = selectedEntry ? `Update ${typeLabel}` : `Add ${typeLabel}`;
  const submitButtonLabel = selectedEntry ? 'Update' : 'Save';

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      message: '',
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    reset({
      message: selectedEntry?.message || '',
    });
  }, [selectedEntry, reset]);

  const handleClose = () => {
    reset({ message: '' });
    onClose();
  };

  const onSubmit = async (data) => {
    const formData = {
      message: data.message,
      section: 'Homepage',
      itemType: typeId,
      releaseDate: DateTime.now().toFormat('yyyy-MM-dd'),
      title: typeLabel,
    };

    if (selectedEntry) {
      await doUpdateContentEntry({ id: selectedEntry.id, ...formData });
    } else {
      await doCreateContentEntry(formData);
    }

    handleClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" trapFocus={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{modalTitle}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form id="editor-form" onSubmit={handleSubmit(onSubmit)}>
            <FormControl isInvalid={errors.message}>
              <FormLabel>Content</FormLabel>
              <Controller
                control={control}
                name="message"
                render={({ field: { onChange, value } }) => (
                  <CKEditor
                    editor={ClassicEditor}
                    config={{
                      toolbar: ['bold', 'italic', 'link'],
                      link: {
                        decorators: {
                          openInNewTab: {
                            mode: 'manual',
                            label: 'Open in a new tab',
                            attributes: {
                              target: '_blank',
                              rel: 'noopener noreferrer',
                            },
                          },
                        },
                      },
                    }}
                    data={value}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      onChange(data);
                    }}
                  />
                )}
              />
              <FormErrorMessage>{errors.message?.message}</FormErrorMessage>
            </FormControl>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="gray" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="editor-form"
            isLoading={isCreating || isUpdating}
          >
            {submitButtonLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default BasicEntryEdit;
